/* eslint-disable @typescript-eslint/naming-convention */
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { AuthRoutes } from '@core/constants/routes.const';
import { PhoneUserRequest, PhoneUserResponse } from '@core/dto/update-phone-singup.dto';
import { Locale } from '@core/enums/locale';
import { AppState } from '@core/store';
import { selectStateIsLogoutConcluded as selectLogOutSuccess } from '@core/store/auth';
import { logOut } from '@core/store/auth/auth.actions';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { isUsaEnv } from '@shared/utils/environment-utils';
import { getLanguage } from '@shared/utils/locale-utils';
import { b2cPolicies } from 'app/auth-config';
import { Observable } from 'rxjs/internal/Observable';
import { filter, first, take } from 'rxjs/operators';
import { LoggerService } from './logger.service';

const COMMON_SCOPES = ['openid', 'offline_access', environment.b2c.customerScope];

@Injectable({ providedIn: 'root' })
@Injectable({ providedIn: 'root' })
export class AuthService {
  registrationData$: Observable<{
    request: PhoneUserRequest | null;
    response: PhoneUserResponse | null;
  }>;

  public isUsaEnv = isUsaEnv;
  private redirectUri: string;

  constructor(
    private msalService: MsalService,
    private store$: Store<AppState>,
    private loggerService: LoggerService,
    private msal$: MsalBroadcastService,
    @Inject(LOCALE_ID) private localeId: Locale,
  ) {
    this.redirectUri =
      this.localeId === Locale.En
        ? `/${AuthRoutes.B2CSigninTarget}`
        : `/${this.localeId}/${AuthRoutes.B2CSigninTarget}`;
  }

  async setActiveAccount(): Promise<void> {
    const accounts = this.msalService.instance.getAllAccounts();

    const hasActive = this.msalService.instance.getActiveAccount();
    if (accounts.length && !hasActive) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  logIn(extraQueryParameters: { [key: string]: string } = {}): void {
    this.msalService.instance.loginRedirect({
      scopes: COMMON_SCOPES,
      extraQueryParameters: {
        ui_locales: getLanguage(this.localeId),
        ...extraQueryParameters,
      },
      redirectUri: this.redirectUri,
    });
  }

  logInPhone(): void {
    this.msalService.instance.loginRedirect({
      scopes: COMMON_SCOPES,
      extraQueryParameters: { ui_locales: getLanguage(this.localeId) },
      redirectUri: this.redirectUri,
      authority: b2cPolicies.authorities.signInPhone.authority,
    });
  }

  signUpPhone(): void {
    const storedData = sessionStorage.getItem('registrationData');

    if (storedData) {
      const { request, response } = JSON.parse(storedData);
      this.redirectWithData(request, response);
      return;
    }
  }

  logOut(): void {
    this.store$.dispatch(logOut());
    this.store$
      .select(selectLogOutSuccess)
      .pipe(
        filter((s) => s),
        first(),
      )
      .subscribe(() => this.msalService.logoutRedirect());
    sessionStorage.clear();
  }

  refreshToken(): void {
    this.msalService.instance.acquireTokenSilent({ scopes: COMMON_SCOPES }).catch((error) => {
      this.loggerService.warn('Error refreshing token silently. Logging out', error);
      this.logOut();
    });
    this.msalService.instance.acquireTokenSilent({ scopes: COMMON_SCOPES }).catch((error) => {
      this.loggerService.warn('Error refreshing token silently. Logging out', error);
      this.logOut();
    });
  }

  private redirectWithData(request: PhoneUserRequest, response: PhoneUserResponse): void {
    this.msal$.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
      )
      .subscribe(() => {
        this.redirectToB2C(request, response);
        // sessionStorage.removeItem('registrationData');
      });
  }

  private redirectToB2C(request: PhoneUserRequest, response: PhoneUserResponse): void {
    const phoneNumber = '%2B' + request.phoneNumber.replace(/\+/g, '');
    const redirectRequest: RedirectRequest = {
      scopes: COMMON_SCOPES,
      authority: b2cPolicies.authorities.signupPhoneNumber.authority,
      redirectUri: this.redirectUri,
      extraQueryParameters: {
        ui_locales: getLanguage(this.localeId),
        givenName: request.firstName || '',
        surname: request.lastName || '',
        phoneNumber: phoneNumber || '',
        isSubscribe: String(request.newsletters || false),
        isShare: String(request.shareWithConsultant || false),
        sitefinityUserId: response.siteFinityUserId || '',
      },
    };
    this.msalService.instance.loginRedirect(redirectRequest);
  }
}
